import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CURRENCIES,
  NOTIFICATION_TYPES,
} from '@savgroup-front-common/constants';
import { LinkButton } from '@savgroup-front-common/core/atoms/link';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import { formatStripeAmount } from '@savgroup-front-common/core/formatters/intl';
import { buildNotification } from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import {
  useBanners,
  useToasts,
} from '@savgroup-front-common/core/molecules/NotificationsProvider';

import { Kind } from '@/api/Payment/getPaymentSessionQuery';

import { useConfirmPaymentIntent } from '../hooks/useConfirmPaymentIntent';
import useCreatePaymentIntent from '../hooks/useCreatePaymentIntent';

import { useGetPaymentSession } from './hooks/useGetPaymentSession';
import messages from './messages';
import { $PaylineWidgetContainer, $Total } from './PaymentMonext.styles';

interface PaymentMonextProps {
  invoice: { id: string; priceWithTax: number; currencyTrigram: CURRENCIES };
  onSubmitSuccess: () => Promise<void>;
  kindId: string;
  kind: Kind;
  isFormCompleted: boolean;
}

export const PaymentMonext: FC<PaymentMonextProps> = ({
  invoice,
  onSubmitSuccess,
  kindId,
  kind,
  isFormCompleted,
}) => {
  const { pushErrors } = useToasts();
  const { pushNotification } = useBanners();
  const {
    paymentIntentClientSecret,
    isLoading,
    refetch: handleCreatePaymentIntent,
  } = useCreatePaymentIntent({
    invoiceId: invoice.id,
    onInvoiceAlreadyPaid: onSubmitSuccess,
  });

  const { handleConfirmPaymentIntent } = useConfirmPaymentIntent({
    invoiceId: invoice.id,
    onSuccess: onSubmitSuccess,
  });

  const { isLoading: isGetPaymentSessionLoading, paymentSessionSummary } =
    useGetPaymentSession({
      sessionId: paymentIntentClientSecret,
      kindId,
      kind,
      onConfirmPaymentIntent: handleConfirmPaymentIntent,
      onCreatePaymentIntent: handleCreatePaymentIntent,
    });

  const navigate = useNavigate();

  const handleFinalStateHasBeenReached = useCallback(
    async ({ state }: { state: string }) => {
      const [currentUrl] = window.location.href.split('?');

      if (
        ['PAYMENT_SUCCESS_FORCE_TICKET_DISPLAY', 'PAYMENT_SUCCESS'].includes(
          state,
        )
      ) {
        try {
          await handleConfirmPaymentIntent({});

          navigate(currentUrl);

          return;
        } catch (error: any) {
          pushErrors(error);

          return undefined;
        }
      }

      pushNotification(
        buildNotification({
          message: {
            ...messages.monextPaymentFailure,
            values: {
              retryButton: (
                <LinkButton onClick={() => window.location.reload()}>
                  <SafeFormattedMessageWithoutSpread
                    message={messages.retryButton}
                  />
                </LinkButton>
              ),
            },
          },
          notificationType: NOTIFICATION_TYPES.ERROR,
          options: {
            hollow: true,
          },
        }),
      );
    },
    [navigate, handleConfirmPaymentIntent, pushErrors, pushNotification],
  );

  const handleDidShowState = useCallback(({ state }: { state: string }) => {
    if (
      !['PAYMENT_METHODS_LIST', 'PAYMENT_METHODS_LIST_SHORTCUT'].includes(state)
    ) {
      return;
    }

    const paylineJQuery = (window.Payline as any)?.jQuery;

    if (!paylineJQuery) {
      return;
    }

    paylineJQuery('.PaylineWidget .pl-pay-btn, .PaylineWidget .pl-btn').text(
      safeFormattedIntlString(messages.payButton),
    );
  }, []);

  const [counter, setCounter] = useState(0);

  useEffect(() => {
    if (!paymentIntentClientSecret || isLoading || !window.Payline) {
      const timeoutId = setInterval(() => {
        setCounter(counter + 1);
      }, 500);

      return () => clearInterval(timeoutId);
    }
  }, [counter, isLoading, paymentIntentClientSecret]);

  if (
    !paymentIntentClientSecret ||
    isLoading ||
    !window.Payline ||
    isGetPaymentSessionLoading
  ) {
    return <BaseLoader />;
  }

  if (paymentSessionSummary || !isFormCompleted) {
    return;
  }

  return (
    <>
      <$Total>
        <SafeFormattedMessageWithoutSpread message={messages.totalAmount} />:{' '}
        {formatStripeAmount(invoice.priceWithTax, invoice.currencyTrigram)}
      </$Total>
      <$PaylineWidgetContainer
        data-auto-init={true}
        token={paymentIntentClientSecret}
        template="column"
        embeddedRedirectionAllowed={false}
        onFinalStateHasBeenReached={handleFinalStateHasBeenReached as any}
        onDidShowState={handleDidShowState as any}
      />
    </>
  );
};
